const YIELD_API_KEY = import.meta.env.VITE_YEILD_API_KEY;
const YIELD_BASE_URL = "https://api.yield.xyz/v1";

export interface YieldOpportunity {
  id: string;
  name: string;
  description: string;
  provider: string;
  apy: number;
  apr: number;
  token: string;
  tokenLogo: string;
  inputToken: string;
  inputTokenLogo: string;
  network: string;
  type: string;
  tvlUsd: string;
  minEntry: string;
  cooldownPeriod: number;
  status: { enter: boolean; exit: boolean };
  metadata?: Record<string, any>;
}

export interface YieldAction {
  id: string;
  yieldId: string;
  intent: "enter" | "exit";
  amount: string;
  status: string;
  transactions: YieldActionTransaction[];
  createdAt: string;
}

/**
 * A position the user actually holds, discovered from on-chain balances
 * rather than from the API's action history.
 *
 * `depositedAmount` / `openedAt` are best-effort enrichment from the action
 * log and are empty when the deposit wasn't made through this app — the
 * balance itself is always authoritative.
 */
export interface YieldPosition {
  id: string;
  yieldId: string;
  balance: string;
  depositedAmount: string;
  openedAt: string;
}

export interface YieldActionTransaction {
  id: string;
  title: string;
  network: string;
  status: string;
  type: string;
  unsignedTransaction: string;
  stepIndex: number;
}

async function yieldFetch(endpoint: string, options?: RequestInit) {
  const res = await fetch(`${YIELD_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": YIELD_API_KEY,
      ...(options?.headers || {}),
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Yield API error (${res.status}): ${errorText}`);
  }

  return res.json();
}

export async function fetchYields(): Promise<YieldOpportunity[]> {
  const data = await yieldFetch("/yields?network=base&token=USDC&limit=50");
  return (data.items || []).map((item: any) => ({
    id: item.id,
    name: item.metadata?.name || item.id,
    description: item.metadata?.description || "",
    provider: item.providerId || item.metadata?.provider || "Unknown",
    apy: item.rewardRate?.total ?? item.rewardRates?.[0]?.apy ?? 0,
    apr: item.rewardRate?.components?.[0]?.rate ?? 0,
    token: item.token?.symbol || "USDC",
    tokenLogo: item.token?.logoURI || "",
    inputToken: item.inputTokens?.[0]?.symbol || item.token?.symbol || "USDC",
    inputTokenLogo: item.inputTokens?.[0]?.logoURI || "",
    network: item.network || "base",
    type: item.mechanics?.type || "vault",
    tvlUsd: item.statistics?.tvlUsd || "0",
    minEntry: item.mechanics?.entryLimits?.minimum || "0",
    cooldownPeriod: item.mechanics?.cooldownPeriod?.seconds || 0,
    status: item.status || { enter: true, exit: true },
    metadata: item.metadata,
  }));
}

export async function enterYield(
  yieldId: string,
  address: string,
  amount: string
): Promise<YieldAction> {
  return yieldFetch("/actions/enter", {
    method: "POST",
    body: JSON.stringify({
      yieldId,
      address,
      arguments: { amount },
    }),
  });
}

export async function exitYield(
  yieldId: string,
  address: string,
  amount: string
): Promise<YieldAction> {
  return yieldFetch("/actions/exit", {
    method: "POST",
    body: JSON.stringify({
      yieldId,
      address,
      arguments: { amount },
    }),
  });
}

export async function getYieldBalance(
  yieldId: string,
  address: string
): Promise<string> {
  const data = await yieldFetch(
    `/yields/${yieldId}/balances?address=${address}`
  );
  const balances = data.items || data.balances || data.data || [];
  if (Array.isArray(balances) && balances.length > 0) {
    // Prefer the withdrawable principal. `active`/`staked` is the position
    // itself; other entries (rewards, pending) are not directly exitable.
    const active =
      balances.find((b: any) => b.type === "active" || b.type === "staked") ||
      balances.find((b: any) => parseFloat(b.amount || "0") > 0) ||
      balances[0];
    return active?.amount || active?.balance || "0";
  }
  return data.amount || data.balance || "0";
}

/** Run `worker` over `items` with a bounded number of in-flight requests. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await worker(items[i]);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * The action log, keyed by yieldId — used only to enrich a position with the
 * original deposit size and date. Never used to decide whether a position exists:
 * it only records actions created through this app, and the API prunes them,
 * so a real vault balance can easily have no action behind it.
 */
async function getEnterActionsByYieldId(
  address: string
): Promise<Map<string, YieldAction>> {
  const byYieldId = new Map<string, YieldAction>();
  try {
    const data = await yieldFetch(`/actions?address=${address}&limit=50`);
    const actions: YieldAction[] = data.items || [];
    for (const action of actions.filter((a) => a.intent === "enter")) {
      const existing = byYieldId.get(action.yieldId);
      if (
        !existing ||
        new Date(action.createdAt) > new Date(existing.createdAt)
      ) {
        byYieldId.set(action.yieldId, action);
      }
    }
  } catch {
    /* enrichment only — a failure here must not hide the user's positions */
  }
  return byYieldId;
}

/**
 * Discover every vault the address actually holds value in, by querying the
 * live balance of each yield in the catalogue.
 *
 * This is the source of truth for "what do I own". The API exposes no bulk
 * balance endpoint, so the catalogue is fanned out with bounded concurrency.
 * A single failing lookup is skipped rather than failing the whole sweep.
 */
export async function discoverPositions(
  address: string,
  yieldIds: string[],
  minBalance = 0.01
): Promise<YieldPosition[]> {
  const [enterActions, balances] = await Promise.all([
    getEnterActionsByYieldId(address),
    mapWithConcurrency(yieldIds, 8, async (yieldId) => {
      try {
        return { yieldId, balance: await getYieldBalance(yieldId, address) };
      } catch {
        return { yieldId, balance: "0" };
      }
    }),
  ]);

  return balances
    .filter(({ balance }) => parseFloat(balance || "0") >= minBalance)
    .map(({ yieldId, balance }) => {
      const action = enterActions.get(yieldId);
      return {
        id: action?.id || `position-${yieldId}`,
        yieldId,
        balance,
        depositedAmount: action?.amount || "",
        openedAt: action?.createdAt || "",
      };
    })
    .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance));
}
